/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import type { CalendarDate } from '@internationalized/date';

import { useFlags } from 'sarsaparilla';

import { FlexDatePicker } from 'shared-ui/flex-date-picker';
import type { FlexDateFilters } from 'shared-ui/flex-date-picker/src/FlexDatePicker.types';

import { FlexDates } from 'ui-navigation/src/components/homepage/FlexDates/FlexDates';
import type { FiltersProps } from 'ui-navigation/src/components/homepage/FlexDates/FlexDates.types';
import {
    legacyFiltersEqual,
    toFlexDateFilters,
    toLegacyFilters,
} from 'ui-navigation/src/components/homepage/FlexDates/FlexDates.utils';
import { SearchCriteriaDates } from '../components/SearchCriteriaDates';

interface SearchDatesProps {
    onLegacyFiltersChange: (filters: FiltersProps) => void;
    legacyFlexFilters: FiltersProps;
    legacyOnApplyFilters?: () => void;
    legacyOnDatesChange: (dates: {
        startDate: CalendarDate | null;
        endDate: CalendarDate | null;
    }) => void;
    isFlexibleAllowed: boolean;
}

/**
 * Renders the new flex date picker beside the legacy date controls.
 * Both read and write the same search-header filter object.
 */
export function SearchDates({
    onLegacyFiltersChange,
    legacyFlexFilters,
    legacyOnApplyFilters,
    legacyOnDatesChange,
    isFlexibleAllowed,
}: SearchDatesProps) {
    const { newSearchDatePicker } = useFlags();

    const handleFlexDateChange = (value: FlexDateFilters) => {
        const nextFilters = toLegacyFilters(value);
        if (legacyFiltersEqual(nextFilters, legacyFlexFilters)) {
            return;
        }

        onLegacyFiltersChange(nextFilters);
    };

    if (newSearchDatePicker) {
        return (
            <FlexDatePicker
                value={toFlexDateFilters(legacyFlexFilters)}
                onChange={handleFlexDateChange}
                isFlexibleAllowed={isFlexibleAllowed}
                popoverAlign="start"
            />
        );
    }

    return (
        <>
            {isFlexibleAllowed ? (
                <div className="search-flex-wrapper">
                    <FlexDates
                        monthsLimit={4}
                        onFiltersChange={onLegacyFiltersChange}
                        filters={legacyFlexFilters}
                        isSearch
                        showClear
                    />
                </div>
            ) : (
                <div className="date-range-picker-wrap">
                    <SearchCriteriaDates
                        onApplyFilters={legacyOnApplyFilters}
                        onDatesChange={legacyOnDatesChange}
                    />
                </div>
            )}
        </>
    );
}
